import React, { useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Input } from '@/components/ui/input';
import { ArrowRight, Shield } from 'lucide-react';
import TurnstileWidget from '@/components/TurnstileWidget';

export default function LoginPage() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState('');
  const [honeypot] = useState('');

  const onCaptchaVerify = useCallback((token) => setCaptchaToken(token), []);
  const onCaptchaExpire = useCallback(() => setCaptchaToken(''), []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!captchaToken) { setError('Please complete the CAPTCHA verification'); return; }
    setLoading(true);
    const result = await login(email, password, captchaToken, honeypot);
    setLoading(false);
    if (result.success) {
      navigate(result.data.role === 'admin' ? '/admin' : '/dashboard');
    } else { setError(result.error); }
  };

  return (
    <div className="min-h-screen bg-[#FFFEF9] flex items-center justify-center px-4" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 rounded-lg overflow-hidden flex items-center justify-center">
              <img src="/bb-logo.jpeg" alt="BuildBotics" className="w-full h-full object-contain" />
            </div>
          </Link>
          <h1 className="text-2xl font-bold text-[#1a1a2e]" style={{ fontFamily: 'Outfit' }}>Welcome back</h1>
          <p className="text-sm text-gray-500 mt-1">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="neo-card-static p-8 space-y-5" data-testid="login-form">
          {error && <div className="bg-red-50 border border-red-200 text-red-600 text-sm px-4 py-3 rounded-lg" data-testid="login-error">{error}</div>}
          {/* Honeypot - hidden from users, visible to bots */}
          <div style={{ position: 'absolute', left: '-9999px', opacity: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
            <input type="text" name="fax_number" tabIndex={-1} autoComplete="new-password"
              value="" onChange={() => {}} readOnly />
          </div>
          <div>
            <label className="text-sm font-medium text-[#1a1a2e] mb-1.5 block">Email</label>
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="you@email.com" required
              className="bg-[#FEFCE8] border-gray-200 text-[#1a1a2e] focus:border-[#0891B2] focus:ring-[#0891B2] h-11" data-testid="login-email-input" />
          </div>
          <div>
            <label className="text-sm font-medium text-[#1a1a2e] mb-1.5 block">Password</label>
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Enter your password" required
              className="bg-[#FEFCE8] border-gray-200 text-[#1a1a2e] focus:border-[#0891B2] focus:ring-[#0891B2] h-11" data-testid="login-password-input" />
            <div className="text-right mt-1.5">
              <Link to="/forgot-password" className="text-xs text-[#0891B2] hover:underline" data-testid="forgot-password-link">Forgot password?</Link>
            </div>
          </div>
          <TurnstileWidget onVerify={onCaptchaVerify} onExpire={onCaptchaExpire} />
          <button type="submit" disabled={loading} className="btn-cyan w-full flex items-center justify-center gap-2 text-sm !rounded-xl" data-testid="login-form-submit-button">
            {loading ? 'Signing in...' : <><Shield className="w-4 h-4" /><span>SIGN IN</span> <ArrowRight className="w-4 h-4" /></>}
          </button>
        </form>

        <p className="text-center text-sm text-gray-500 mt-6">
          Don't have an account?{' '}
          <Link to="/register" className="text-[#0891B2] hover:underline font-medium" data-testid="login-register-link">Create one</Link>
        </p>
      </div>
    </div>
  );
}
